// =====================
// SOP step display (shared wording)
// =====================
//
// Turns a recorded SopStep[] into the human-readable numbered items shown on
// the review page and written into the saved SOP article's Procedure section.
// Shared so the reviewer preview and the server-built article always use
// identical wording (docs/plans/sop-capture-extension/plan.md, Slice A).

import { SYSTEM_KB_TEMPLATES } from '../../types/knowledge-base'
import type {
  SopNavigateStep,
  SopPolishInstruction,
  SopStep,
  SopStepReviewEdit,
  SopTabStep,
} from '../../types/sop-recording'

export type SopNavigationStep = SopNavigateStep | SopTabStep

/**
 * A step after the review pass: input steps may carry the example value the
 * reviewer typed (captured values never exist — recording-format.md).
 */
export type SopReviewedStep = SopStep & { exampleValue?: string }

export type SopDisplayItem =
  | { kind: 'step'; step: SopStep }
  | { kind: 'navigation'; origin: string; visits: SopNavigationStep[] }

/**
 * Apply review-pass edits: dropped steps are removed, example values attach
 * to their input steps. Used by both the review-page preview and the server's
 * save-as-article so the article wording always matches what was previewed.
 */
export function applySopReviewEdits(
  steps: SopStep[],
  edits?: SopStepReviewEdit[],
): SopReviewedStep[] {
  if (!edits?.length) return steps
  const byIndex = new Map(edits.map(edit => [edit.index, edit]))
  const reviewed: SopReviewedStep[] = []
  steps.forEach((step, index) => {
    const edit = byIndex.get(index)
    if (edit?.dropped) return
    reviewed.push(
      edit?.exampleValue && step.type === 'input'
        ? { ...step, exampleValue: edit.exampleValue }
        : step,
    )
  })
  return reviewed
}

export function isNavigationStep(step: SopStep): step is SopNavigationStep {
  return step.type === 'navigate' || step.type === 'tab'
}

// Consecutive navigate/tab steps on the same origin collapse into one
// numbered "Go to <origin>" item; a different origin starts a new item.
export function buildDisplayItems(steps: SopStep[]): SopDisplayItem[] {
  const items: SopDisplayItem[] = []
  for (const step of steps) {
    if (!isNavigationStep(step)) {
      items.push({ kind: 'step', step })
      continue
    }
    const last = items[items.length - 1]
    if (last?.kind === 'navigation' && last.origin === step.origin) {
      last.visits.push(step)
    } else {
      items.push({ kind: 'navigation', origin: step.origin, visits: [step] })
    }
  }
  return items
}

export function describeStep(
  step: SopReviewedStep,
  exampleValue?: string,
): string {
  const label = 'label' in step && step.label ? `"${step.label}"` : null
  switch (step.type) {
    case 'click':
      return label ? `Click ${label}` : 'Click (unlabeled element)'
    case 'input': {
      // Reviewer-typed example (never a captured value); the explicit param
      // lets the review page preview an edit before it's applied.
      const example = exampleValue ?? step.exampleValue
      if (example) {
        return label
          ? `Enter "${example}" in ${label}`
          : `Enter "${example}"`
      }
      return `Enter ${label ?? 'a value'} (${step.maskedLength} ${
        step.maskedLength === 1 ? 'character' : 'characters'
      })`
    }
    case 'select':
      return `Select "${step.option}" in ${label ?? 'a list'}`
    case 'toggle':
      return `${step.checked ? 'Check' : 'Uncheck'} ${label ?? `a ${step.control}`}`
    case 'navigate':
      return step.how === 'load' ? 'Page load' : `Navigate (${step.how})`
    case 'tab':
      return step.action === 'opened' ? 'Open a new tab' : 'Switch tab'
  }
}

export function describeVisit(visit: SopNavigationStep): string {
  if (visit.type === 'tab') {
    return `${visit.action === 'opened' ? 'New tab' : 'Switch tab'} · ${visit.url}`
  }
  return visit.url
}

// Step labels/options/origins are page-derived text; escape before embedding
// in article HTML.
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export interface SopArticleHtmlOptions {
  /**
   * Article-namespace copies of approved screenshots, keyed by the step's
   * staging screenshotKey. Steps whose key maps emit an s3Image-parseable
   * `<img data-s3-key>` (src stays empty; viewers resolve signed URLs from
   * the key). The copies arrive with blur and the click marker already baked
   * in — this HTML carries no overlay of its own. A frame
   * shared by several rapid steps is emitted once, at its first reference.
   */
  imageKeys?: Record<string, { s3Key: string; filename: string }>
  /**
   * The article template the recording is being saved onto. Defaults to the
   * built-in SOP scaffold; the server passes a system template's html or an
   * org template article's contentHtml when the reviewer picks another one.
   * Recorded steps replace the template's first ordered list (every built-in
   * scaffold uses one for its step section), and an AI summary replaces the
   * paragraph under the template's first heading.
   */
  templateHtml?: string
}

/**
 * Where recorded steps land in a template: its first `<ol>`. Templates are
 * authored with a placeholder step list (Procedure, Steps), and `<ul>` is
 * used for unordered scaffolding like Prerequisites, so the first ordered
 * list is unambiguously the step section. A template without one gets the
 * steps appended under a Steps heading rather than silently losing them.
 *
 * Replacement uses a function so `$&`-style sequences in step text (labels,
 * filenames) are never interpreted as replacement patterns.
 */
function replaceStepList(templateHtml: string, listHtml: string): string {
  const stepList = /<ol>[\s\S]*?<\/ol>/
  if (!stepList.test(templateHtml)) {
    return `${templateHtml}<h2>Steps</h2>${listHtml}`
  }
  return templateHtml.replace(stepList, () => listHtml)
}

/**
 * Where an AI summary lands: the paragraph under the template's first
 * heading (Purpose on the SOP scaffold, Overview on the runbook, What this
 * covers on the how-to). Templates whose first section is not a paragraph
 * get the summary as a lead paragraph above everything.
 */
function replaceLeadParagraph(templateHtml: string, summary: string): string {
  const leadParagraph = /(<h2>[\s\S]*?<\/h2>)<p>[\s\S]*?<\/p>/
  const paragraph = `<p>${escapeHtml(summary)}</p>`
  if (!leadParagraph.test(templateHtml)) {
    return `${paragraph}${templateHtml}`
  }
  return templateHtml.replace(leadParagraph, (_match, heading) =>
    typeof heading === 'string' ? `${heading}${paragraph}` : paragraph,
  )
}

/** The built-in SOP scaffold, the default destination for a recording. */
function defaultTemplateHtml(): string {
  const template = SYSTEM_KB_TEMPLATES.find(t => t.id === 'system:sop')
  if (!template) throw new Error('SOP system template not found')
  return template.html
}

export function buildSopProcedureHtml(
  steps: SopReviewedStep[],
  options: SopArticleHtmlOptions = {},
): string {
  const emittedKeys = new Set<string>()
  const items = buildDisplayItems(steps).map(item => {
    if (item.kind === 'navigation') {
      return `<li><p>Go to ${escapeHtml(item.origin)}</p></li>`
    }
    const text = describeStep(item.step)
    const image =
      item.step.screenshotKey && !emittedKeys.has(item.step.screenshotKey)
        ? options.imageKeys?.[item.step.screenshotKey]
        : undefined
    if (!image) {
      return `<li><p>${escapeHtml(text)}</p></li>`
    }
    emittedKeys.add(item.step.screenshotKey!)
    return (
      `<li><p>${escapeHtml(text)}</p>` +
      `<img src="" data-s3-key="${escapeHtml(image.s3Key)}" ` +
      `data-filename="${escapeHtml(image.filename)}" ` +
      `alt="${escapeHtml(text)}"></li>`
    )
  })
  return `<ol>${items.join('')}</ol>`
}

/**
 * The chosen article template with its placeholder step list replaced by the
 * recorded steps. Every other section keeps its scaffold placeholder text for
 * the author to fill in. Defaults to the built-in SOP scaffold.
 */
export function buildSopArticleHtml(
  steps: SopReviewedStep[],
  options: SopArticleHtmlOptions = {},
): string {
  return replaceStepList(
    options.templateHtml ?? defaultTemplateHtml(),
    buildSopProcedureHtml(steps, options),
  )
}

/**
 * The wording an AI-polished instruction shows on the review page and writes
 * into the article: the polished text, plus ` (e.g. "value")` for each of its
 * input steps carrying a reviewer-typed example value. Shared so the preview
 * always matches the saved article (the invariant describeStep upholds for
 * the raw path). Example values by RAW step index — the same keying as
 * SopStepReviewEdit.
 */
export function polishedInstructionText(
  instruction: SopPolishInstruction,
  steps: SopStep[],
  exampleValuesByIndex?: Record<number, string | undefined>,
): string {
  if (!exampleValuesByIndex) return instruction.text
  let text = instruction.text
  for (const index of instruction.stepIndexes) {
    const example = exampleValuesByIndex[index]?.trim()
    if (example && steps[index]?.type === 'input') {
      text += ` (e.g. "${example}")`
    }
  }
  return text
}

/**
 * Polished-path article body: the chosen template with its lead paragraph
 * replaced by the AI summary and its step list replaced by the polished
 * instructions. Instruction/summary text is untrusted model output headed
 * into article HTML, so everything is escaped here. Pure and deterministic —
 * save-as-article calls it twice (text-only phase, then with imageKeys).
 * An instruction whose screenshotKey is absent from options.imageKeys
 * degrades to text-only, matching buildSopProcedureHtml; a shared frame is
 * emitted once, at its first reference.
 */
export function buildSopPolishedArticleHtml(
  instructions: { text: string; screenshotKey?: string }[],
  summary: string,
  options: SopArticleHtmlOptions = {},
): string {
  const emittedKeys = new Set<string>()
  const items = instructions.map(instruction => {
    const image =
      instruction.screenshotKey && !emittedKeys.has(instruction.screenshotKey)
        ? options.imageKeys?.[instruction.screenshotKey]
        : undefined
    if (!image) {
      return `<li><p>${escapeHtml(instruction.text)}</p></li>`
    }
    emittedKeys.add(instruction.screenshotKey!)
    return (
      `<li><p>${escapeHtml(instruction.text)}</p>` +
      `<img src="" data-s3-key="${escapeHtml(image.s3Key)}" ` +
      `data-filename="${escapeHtml(image.filename)}" ` +
      `alt="${escapeHtml(instruction.text)}"></li>`
    )
  })
  let html = replaceStepList(
    options.templateHtml ?? defaultTemplateHtml(),
    `<ol>${items.join('')}</ol>`,
  )
  const trimmedSummary = summary.trim()
  if (trimmedSummary) {
    html = replaceLeadParagraph(html, trimmedSummary)
  }
  return html
}
